import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  LinearProgress,
  CircularProgress,
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  CleaningServices as ServiceIcon,
  PlayArrow as StartIcon,
  CheckCircle as DoneIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  InfoOutlined as InfoIcon,
  PersonRemove as UnclaimIcon,
  DirectionsCar as EnRouteIcon,
  Payments as PayoutIcon,
} from '@mui/icons-material';
import axios from 'axios';
import logger from '../../utils/logger';
import PropertyInfoDisplay from './PropertyInfoDisplay';
import JobChat from '../JobChat';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_CONFIG = {
  NOT_STARTED: { label: 'Not Started', color: 'warning' },
  IN_PROGRESS: { label: 'In Progress', color: 'info' },
  COMPLETED:   { label: 'Completed',   color: 'success' },
  CANCELLED:   { label: 'Cancelled',   color: 'error' },
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const formatTime = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
};

/**
 * JobExecutionScreen — mobile-first detail home for a single job.
 *
 * Layout regions (top → bottom):
 *   1. Header       — customer, time, address, service, special instructions
 *   2. Status bar   — status chip + progress bar
 *   3. Action bar   — En route → Start → Complete
 *   4. Task list    — grouped by category, checkable when IN_PROGRESS
 *   5. (Footer reserved for future sign-off step)
 *
 * Phase A will extend the Action bar without touching this skeleton.
 */
const JobExecutionScreen = ({ jobId, onJobComplete }) => {
  const [job, setJob] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [actionLoading, setActionLoading] = useState(false);
  const [unclaimLoading, setUnclaimLoading] = useState(false);
  const [unclaimDialogOpen, setUnclaimDialogOpen] = useState(false);
  const [taskUpdating, setTaskUpdating] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});

  // ---------- data ----------
  const fetchJob = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [jobRes, tasksRes] = await Promise.all([
        axios.get(`${API_URL}/api/jobs/${jobId}`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/jobs/${jobId}/tasks`, { headers: authHeaders() }),
      ]);
      setJob(jobRes.data);
      setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
    } catch (err) {
      logger.log('Error fetching job:', err);
      setError('Could not load this job. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) fetchJob();
  }, [jobId, fetchJob]);

  // Auto-expand the first category once tasks land
  useEffect(() => {
    if (tasks.length === 0) return;
    setExpandedCats((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const firstCat = tasks[0].category || 'General';
      return { [firstCat]: true };
    });
  }, [tasks]);

  // ---------- derived ----------
  const grouped = useMemo(() => {
    const out = {};
    tasks.forEach((t) => {
      const cat = t.category || 'General';
      if (!out[cat]) out[cat] = [];
      out[cat].push(t);
    });
    return out;
  }, [tasks]);

  const completedCount = tasks.filter((t) => t.isCompleted).length;
  const totalCount = tasks.length;
  const requiredRemaining = tasks.filter((t) => t.isRequired && !t.isCompleted).length;
  const canComplete = job?.status === 'IN_PROGRESS' && requiredRemaining === 0;

  const statusCfg = job
    ? STATUS_CONFIG[job.status] || { label: job.status, color: 'default' }
    : { label: '—', color: 'default' };

  // ---------- actions ----------
  const runJobAction = async (action) => {
    setActionLoading(action);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/jobs/${jobId}/${action}`,
        {},
        { headers: authHeaders() }
      );
      await fetchJob();
      if (action === 'complete' && onJobComplete) onJobComplete();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} job.`);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleTask = async (task) => {
    if (taskUpdating || job?.status !== 'IN_PROGRESS') return;
    setTaskUpdating(task.id);
    const newValue = !task.isCompleted;
    // Optimistic
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, isCompleted: newValue } : t)));
    try {
      await axios.patch(
        `${API_URL}/api/jobs/${jobId}/tasks/${task.id}`,
        { isCompleted: newValue },
        { headers: authHeaders() }
      );
    } catch (err) {
      // Revert
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, isCompleted: task.isCompleted } : t)));
      setError('Could not update that task. Try again.');
    } finally {
      setTaskUpdating(null);
    }
  };

  const handleUnclaim = async () => {
    setUnclaimLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_URL}/api/jobs/${jobId}/claim`, { headers: authHeaders() });
      setUnclaimDialogOpen(false);
      if (onJobComplete) onJobComplete();
    } catch (err) {
      setUnclaimDialogOpen(false);
      setError(err.response?.data?.error || 'Failed to unclaim job.');
    } finally {
      setUnclaimLoading(false);
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCats((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  // ---------- render ----------
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!job) {
    return <Alert severity="error">Job not found.</Alert>;
  }

  const customerName =
    job.customer?.customerName ||
    `${job.booking?.client?.firstName || ''} ${job.booking?.client?.lastName || ''}`.trim() ||
    'Customer';

  const address =
    job.customer?.customerAddress ||
    (job.address
      ? [job.address.street, job.address.city, job.address.state, job.address.zip].filter(Boolean).join(', ')
      : null);

  const scheduledDateLabel = formatDate(job.utcScheduledDate);
  const startTime = formatTime(job.utcScheduledDate);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ============ Header ============ */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {customerName}
              </Typography>
              {job.jobNumber && (
                <Typography variant="caption" color="text.secondary">
                  Job #{job.jobNumber}
                </Typography>
              )}
            </Box>
            <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
          </Box>

          {scheduledDateLabel && (
            <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
              <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {scheduledDateLabel}{startTime ? ` · ${startTime}` : ''}
              </Typography>
            </Box>
          )}

          {address && (
            <Box display="flex" alignItems="flex-start" gap={0.75} mb={0.5}>
              <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mt: '2px' }} />
              <Typography variant="body2" color="text.secondary">{address}</Typography>
            </Box>
          )}

          {(job.serviceType || job.jobType) && (
            <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
              <ServiceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {(job.serviceType || job.jobType)
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
              </Typography>
            </Box>
          )}

          {job.myAssignment?.paymentAmount != null && (
            <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
              <PayoutIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2" fontWeight={600} color="success.main">
                Your payout ${Number(job.myAssignment.paymentAmount).toFixed(2)}
                {job.myAssignment.paid ? ' · paid' : ''}
              </Typography>
            </Box>
          )}

          {/* Several services on this job: who does what */}
          {((job.lineItems || []).length > 1 || (job.lineItems || []).some((l) => l.technicianId)) && (() => {
            const myId = (job.assignments || []).find((a) => a.id === job.myAssignment?.id)?.maidId;
            return (
              <Box mt={1.5}>
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Services
                </Typography>
                {[...job.lineItems].sort((a, b) => a.position - b.position).map((line) => {
                  const mine = !!myId && line.technicianId === myId;
                  const label = line.kind === 'MAIN'
                    ? (job.serviceType ? job.serviceType.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : 'Cleaning')
                    : line.description;
                  const doneBy = !line.technicianId
                    ? 'Whole team'
                    : mine
                      ? 'You'
                      : [line.technician?.firstName, line.technician?.lastName].filter(Boolean).join(' ') || 'Teammate';
                  return (
                    <Box key={line.id} display="flex" justifyContent="space-between" gap={2}>
                      <Typography variant="body2" sx={mine ? { fontWeight: 700, color: 'success.dark' } : undefined}>
                        {label}{Number(line.quantity) > 1 ? ` × ${Number(line.quantity)}` : ''}
                      </Typography>
                      <Typography variant="body2" color={mine ? 'success.dark' : 'text.secondary'}>
                        {doneBy}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}

          {/* Property info (thumbnail variant) — renders nothing if propertyInfo is empty */}
          <Box mt={1.5}>
            <PropertyInfoDisplay propertyInfo={job.propertyInfo} photo="thumbnail" />
          </Box>

          {job.specialInstructions && (
            <Box mt={1.5} p={1.25} bgcolor="warning.50" sx={{ borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
              <Box display="flex" alignItems="flex-start" gap={0.75}>
                <InfoIcon sx={{ fontSize: 16, color: 'warning.dark', mt: '2px' }} />
                <Box>
                  <Typography variant="caption" fontWeight={700} color="warning.dark"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Special instructions
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                    {job.specialInstructions}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ============ Progress ============ */}
      {totalCount > 0 && (
        <Box mb={1.5}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Progress
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {completedCount} / {totalCount} tasks
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(completedCount / totalCount) * 100}
            sx={{ height: 6, borderRadius: 3 }}
            color={completedCount === totalCount ? 'success' : 'primary'}
          />
        </Box>
      )}

      {/* ============ Action bar (Phase A mounts more buttons here) ============ */}
      <Box mb={2}>
        {job.status === 'NOT_STARTED' && (
          <>
            {/* Pre-start step: En route (then Start Job) */}
            <Box display="flex" gap={1} mb={1}>
              <Button
                fullWidth
                variant={job.utcEnRouteTime ? 'contained' : 'outlined'}
                color={job.utcEnRouteTime ? 'success' : 'primary'}
                startIcon={actionLoading === 'en-route'
                  ? <CircularProgress size={14} color="inherit" />
                  : job.utcEnRouteTime ? <DoneIcon /> : <EnRouteIcon />}
                onClick={() => runJobAction('en-route')}
                disabled={Boolean(actionLoading) || unclaimLoading || Boolean(job.utcEnRouteTime)}
                sx={job.utcEnRouteTime ? { pointerEvents: 'none' } : undefined}
              >
                {job.utcEnRouteTime ? 'En route ✓' : "I'm en route"}
              </Button>
            </Box>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              size="large"
              startIcon={actionLoading === 'start' ? <CircularProgress size={16} color="inherit" /> : <StartIcon />}
              onClick={() => runJobAction('start')}
              disabled={Boolean(actionLoading) || unclaimLoading}
            >
              {actionLoading === 'start' ? 'Starting…' : 'Start Job'}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              size="small"
              sx={{ mt: 1 }}
              startIcon={unclaimLoading ? <CircularProgress size={14} color="inherit" /> : <UnclaimIcon />}
              onClick={() => setUnclaimDialogOpen(true)}
              disabled={actionLoading || unclaimLoading}
            >
              {unclaimLoading ? 'Unclaiming…' : 'Unclaim Job'}
            </Button>
          </>
        )}

        {job.status === 'IN_PROGRESS' && (
          <>
            {!canComplete && requiredRemaining > 0 && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {requiredRemaining} required task{requiredRemaining === 1 ? '' : 's'} remaining
              </Alert>
            )}
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="large"
              startIcon={actionLoading === 'complete' ? <CircularProgress size={16} color="inherit" /> : <DoneIcon />}
              onClick={() => runJobAction('complete')}
              disabled={Boolean(actionLoading) || !canComplete}
            >
              {actionLoading === 'complete' ? 'Finishing…' : 'Finish Job'}
            </Button>
          </>
        )}

        {job.status === 'COMPLETED' && (
          <Alert severity="success" icon={<DoneIcon />}>
            Job completed
            {job.actualDuration ? ` in ${job.actualDuration} min` : ''}.
          </Alert>
        )}
      </Box>

      {/* ============ Task list ============ */}
      {totalCount === 0 ? (
        <Alert severity="info">No tasks attached to this job yet.</Alert>
      ) : (
        Object.entries(grouped).map(([cat, catTasks]) => {
          const catDone = catTasks.filter((t) => t.isCompleted).length;
          const catTotal = catTasks.length;
          const open = !!expandedCats[cat];
          return (
            <Card variant="outlined" key={cat} sx={{ mb: 1 }}>
              <Box
                onClick={() => toggleCategory(cat)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.25,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {cat}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {catDone} / {catTotal}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleCategory(cat); }}>
                  {open ? <CollapseIcon /> : <ExpandIcon />}
                </IconButton>
              </Box>
              <Collapse in={open}>
                <Divider />
                <Box px={2} py={1}>
                  {catTasks.map((task) => (
                    <Box key={task.id} display="flex" alignItems="flex-start" gap={0.5} mb={0.25}>
                      <FormControlLabel
                        sx={{ m: 0, flex: 1, alignItems: 'flex-start' }}
                        control={
                          <Checkbox
                            size="small"
                            checked={task.isCompleted}
                            onChange={() => toggleTask(task)}
                            disabled={taskUpdating === task.id || job.status !== 'IN_PROGRESS'}
                            sx={{ pt: 0.5 }}
                          />
                        }
                        label={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration: task.isCompleted ? 'line-through' : 'none',
                                color: task.isCompleted ? 'text.disabled' : 'text.primary',
                                fontWeight: task.isRequired ? 600 : 400,
                              }}
                            >
                              {task.title}
                              {task.isRequired && (
                                <Typography component="span" variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                                  *
                                </Typography>
                              )}
                            </Typography>
                            {task.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {task.description}
                              </Typography>
                            )}
                            {task.estimatedMinutes && (
                              <Typography variant="caption" color="text.secondary">
                                Est. {task.estimatedMinutes} min
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      {taskUpdating === task.id && (
                        <CircularProgress size={14} sx={{ mt: 1, mr: 0.5 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Card>
          );
        })
      )}

      {/* Messaging */}
      <Card sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
        <JobChat jobId={jobId} compact />
      </Card>

      {/* Unclaim confirmation */}
      <Dialog
        open={unclaimDialogOpen}
        onClose={() => !unclaimLoading && setUnclaimDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unclaim this job?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            It will go back to the available jobs pool for other technicians to claim.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnclaimDialogOpen(false)} disabled={unclaimLoading}>
            Keep Job
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleUnclaim}
            disabled={unclaimLoading}
            startIcon={unclaimLoading ? <CircularProgress size={14} color="inherit" /> : <UnclaimIcon />}
          >
            {unclaimLoading ? 'Unclaiming…' : 'Unclaim'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobExecutionScreen;
